import { cn } from '@/lib/utils'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { cva } from 'class-variance-authority'
import { ChevronDownIcon } from 'lucide-react'
import * as React from 'react'

type AccordionVariant = 'default' | 'outlined' | 'ghost'

const AccordionVariantContext = React.createContext<AccordionVariant>('default')

const triggerVariants = cva(
    [
        'flex flex-1 items-center justify-between gap-4 rounded-md px-4 py-3',
        'text-left text-lg font-medium text-foreground',
        'cursor-pointer transition-colors outline-none',
        'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[state=open]:rounded-b-none [&[data-state=open]>svg]:rotate-180',
        'bg-primary/10 dark:bg-primary/20 outline-none border-none',
    ].join(' '),
    {
        variants: {
            variant: {
                default: 'dark:hover:bg-primary/40 hover:bg-primary/30',
                outlined: 'border bg-card hover:bg-accent/50',
                ghost: [
                    'p-0 text-inherit text-base focus:outline-none',
                    'bg-transparent dark:bg-transparent',
                    '[&_svg]:hidden',
                ].join(' '),
            },
        },
        defaultVariants: { variant: 'default' },
    },
)

const contentVariants = cva(
    [
        'overflow-hidden text-sm text-foreground',
        'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
    ].join(' '),
    {
        variants: {
            variant: {
                default: '',
                outlined: 'border border-t-0 shadow-sm',
                ghost: 'text-inherit text-base',
            },
        },
        defaultVariants: { variant: 'default' },
    },
)

const itemVariants = cva('rounded-md', {
    variants: {
        variant: {
            default: 'dark:bg-primary/10',
            outlined: '',
            ghost: 'rounded-none',
        },
    },
    defaultVariants: { variant: 'default' },
})

const Root = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root> & {
        variant?: AccordionVariant
    }
>(({ variant = 'default', ...props }, ref) => (
    <AccordionVariantContext.Provider value={variant}>
        <AccordionPrimitive.Root data-slot="accordion" ref={ref} {...props} />
    </AccordionVariantContext.Provider>
))
Root.displayName = 'ArrowAccordion.Root'

const Item = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => {
    const variant = React.useContext(AccordionVariantContext)

    return (
        <AccordionPrimitive.Item
            data-slot="accordion-item"
            className={cn(itemVariants({ variant }), className)}
            ref={ref}
            {...props}
        />
    )
})
Item.displayName = 'ArrowAccordion.Item'

const Trigger = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
    const variant = React.useContext(AccordionVariantContext)

    return (
        <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger
                data-slot="accordion-trigger"
                className={cn(triggerVariants({ variant }), className)}
                ref={ref}
                {...props}
            >
                {children}
                <ChevronDownIcon className="text-secondary-foreground pointer-events-none size-6 shrink-0 transition-transform duration-200" />
            </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
    )
})
Trigger.displayName = 'ArrowAccordion.Trigger'

const Content = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => {
    const variant = React.useContext(AccordionVariantContext)

    return (
        <AccordionPrimitive.Content
            data-slot="accordion-content"
            className={contentVariants({ variant })}
            ref={ref}
            {...props}
        >
            <div className={cn('pt-0 pb-4', className)}>{children}</div>
        </AccordionPrimitive.Content>
    )
})
Content.displayName = 'ArrowAccordion.Content'

/**
 * Accordion no estilo de setas, com ícone de seta rotacionando ao abrir/fechar.
 *
 * Use `variant="outlined"` no `Root` para o estilo com borda (em vez do
 * padrão elevado/card que reflete o MUI).
 *
 * @example
 * ```tsx
 * <ArrowAccordion.Root type="single" collapsible>
 *   <ArrowAccordion.Item value="item-1">
 *     <ArrowAccordion.Trigger>
 *       Título do Accordion
 *     </ArrowAccordion.Trigger>
 *     <ArrowAccordion.Content>
 *       Conteúdo do Accordion
 *     </ArrowAccordion.Content>
 *   </ArrowAccordion.Item>
 * </ArrowAccordion.Root>
 * ```
 */
export const ArrowAccordion = {
    Root,
    Item,
    Trigger,
    Content,
}
